import { z } from 'zod'
import { profileCompletionSchema } from './profile-schema'
import { profileAddressSchema } from './address-schema'

/** Shared profile metadata; favorite address remains available to setup and shipping. */
const profileFieldsSchema = z.object({
  jastip_name: z.string(),
  gender: z.enum(['', 'PRIA', 'WANITA']),
  job: z.string(),
  favorite_address_id: z.number().int().positive().nullable().default(null),
  favorite_address: profileAddressSchema.nullish(),
  phone_number: z.string(),
  phone_code_id: z.number().int(),
  logo_path: z.string(),
  social_media_urls: z.array(z.string()),
})

/**
 * GET /liotitip/profile. The address moved to `favorite_address` (address API,
 * WEBLP-3321); retain optional flat fields for legacy Profil Toko responses
 * without discarding the favorite object used by setup and order shipping.
 */
export const profileSchema = profileFieldsSchema.extend({
  route_id: z.number().int().nullish(),
  address: z.string().nullish(),
  additional_address: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  favorite_address_label: z.string().optional(),
})

export const saveProfileSchema = profileFieldsSchema
  .omit({
    favorite_address_id: true,
    favorite_address: true,
  })
  .extend({
    jastip_name: z.string().trim().min(1).max(50),
    gender: z.enum(['PRIA', 'WANITA']),
    label: z.string().trim().min(1).max(50),
    route_id: z.number().int().positive(),
    phone_number: z
      .string()
      .trim()
      .regex(/^\+?\d{5,15}$/)
      .max(15),
    address: z.string().trim().min(1).max(200),
    additional_address: z.string().trim().max(100),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    phone_code_id: z.number().int().positive(),
    social_media_urls: z.array(
      z
        .string()
        .trim()
        .refine((value) => new TextEncoder().encode(value).length <= 100),
    ),
  })

export type Profile = z.infer<typeof profileSchema>
export type SaveProfileInput = z.infer<typeof saveProfileSchema>

// WEBLP-3248 may return the normalized flat write payload; GET returns the favorite object.
export const saveProfileResponseSchema = z.union([
  profileSchema,
  saveProfileSchema.omit({ label: true }).extend({ label: z.string().optional() }),
])

export function isProfileComplete(profile: Profile) {
  return (
    profile.favorite_address_id !== null &&
    profileDetailsSchema.safeParse({
      jastipName: profile.jastip_name,
      gender:
        profile.gender === 'PRIA' ? 'Laki-laki' : profile.gender === 'WANITA' ? 'Perempuan' : '',
      occupation: profile.job,
      socialMediaLinks: profile.social_media_urls.map((url) => ({ url })),
    }).success
  )
}

const profileDetailsSchema = profileCompletionSchema.omit({
  address: true,
  additionalAddress: true,
  latitude: true,
  longitude: true,
  destination: true,
})
